import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Serverless performance",
  "description": "How does performance impact your serverless setup? What should you think about?",
  "image": "./img/serverless-performance.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "serverless-performance"
    }}>{`Serverless performance`}</h1>
    <p><img parentName="p" {...{
        "src": "/b6b94cd942ce9dbbd9b43fd4bbf50218/serverless-performance.svg",
        "alt": null
      }}></img></p>
    <p>{`Performance talks about what you get per unit of a resource. Time, effort, space, energy, and money.`}</p>
    <p>{`How much of X does it take to get N results?`}</p>
    <p>{`We've touched on performance before. Mentioned scaling strategies in `}<a parentName="p" {...{
        "href": "/serverless-pros-cons"
      }}>{`Pros and Cons of Serverless`}</a>{` and `}<a parentName="p" {...{
        "href": "/databases"
      }}>{`Databases`}</a>{`, talked about complexity in `}<a parentName="p" {...{
        "href": "/lambda-pipelines"
      }}>{`Lambda Pipelines`}</a>{`, and mentioned flow in `}<a parentName="p" {...{
        "href": "/robust-backend-design"
      }}>{`Robust Backend Design`}</a>{`.`}</p>
    <p>{`But what do you measure? What's achievable? Where do you optimize? How does serverless stack up?`}</p>
    <h2 {...{
      "id": "the-performance-trifecta"
    }}>{`The performance trifecta`}</h2>
    <p>{`Software systems care about time, money, and space. You have to balance all 3.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/82b99149d74ea13e7d8705ee269c48d0/e4ba2/fast-small-cheap.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "60.0896860986547%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'240\\'%20viewBox=\\'0%200%20400%20240\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M146%205l-11%202-17%203c-28%202-61%2038-63%2071v9l-5%202c-14%207-23%2018-33%2039-13%2026-16%2043-10%2061%207%2023%2036%2044%2061%2044%2017%200%2039-9%2059-25l6-4%203%204c20%2025%2057%2030%2095%2015%2029-12%2052-33%2061-57l3-9v-17c0-16%200-18-2-27-9-30-21-40-53-43l-1-2a296%20296%200%200164-36c-2-1-32%2013-54%2026l-10%207-2-7c-8-31-41-55-79-56h-12m-3%202c-15%204-17%204-16%206v1c-4-2-4-2-15%203-16%208-31%2019-36%2028-1%201-1%202-2%201-2-5%2019-22%2037-30%209-4%2010-5%204-4-6%200-23%209-30%2016a82%2082%200%2000-28%2052l1%209c10-4%2034-4%2049%200%2012%203%2019%206%2027%2012l7%205%203-2%202-1%205-3c13-12%2028-19%2049-24l4-1c-23%201-37%208-54%2023-2%203-4%204-4%203%200-2%2017-16%2025-20%2012-6%2040-11%2056-10%209%201%2011-1%207-12-6-23-28-42-57-50-9-2-28-4-34-2m-10%2020l1%2011%203%204%203%201-4%204-5%209c0%203%200%204%201%203%200-1%2023-2%2025%200s1-6-1-10l-5-4-5-2c-2-1-1-1%205-4%208-3%2010-5%2010-10v-2h-28m2%206c0%206%200%206%203%208%203%201%203%201%208-1l8-3c4-2%205-3%205-6v-3h-24v5m2%2016c-6%206-5%206%208%207%2011%200%2011%200%2011-2%200-4-3-7-8-8l-6-2-5%205m71%2028c-24%203-43%2012-59%2027l-5%205%206%207c5%205%207%207%208%206l12-9a1361%201361%200%200155-37c2-1-7-1-17%201m-6%2017c-29%2019-44%2030-44%2031l3%207c3%206%203%206%206%205%2023-7%2032-11%2042-18%2017-10%2026-23%2028-38v-6h-3c-3%200-9%203-32%2019m36-11c-4%2025-26%2043-65%2054l-8%203v10c0%2017-6%2032-22%2048-9%209-9%209%200%2018%2014%2012%2025%2016%2046%2016%2045%200%2094-33%20103-71a115%20115%200%2000-1-43c-4-26-20-40-47-42l-5-1-1%208M63%2089c-5%201-8%204-3%203%203-1%2011%200%2012%202h-2l-8-1h-4l1%203c4%2024%2029%2044%2055%2045h5l5-9a151%20151%200%200115-24c1-1-12-10-22-14-15-5-41-8-54-5m-15%209c-21%2012-40%2045-42%2073-1%2017%204%2029%2016%2042%2029%2029%2064%2028%20105-4l5-3-3-5c-12-17-17-38-11-53l1-5h-4c-5%200-17-3-24-6-16-7-28-20-33-36l-3-7-7%204m95%2021l-9%202-5%205c-3%205-3%205%209%200l12-4c2-1%202-3%200-4l-7%201m3%206c-17%206-20%208-22%2011l-2%205c0%202%208%201%2014-1a136%20136%200%200122-6l-3%202a1706%201706%200%2001-17%206l23-4c1-1-4-12-5-12l-1%202-3%202c-2%200-3%201-3%202l-2%201c-1-1-6%200-10%202-10%203-12%204-12%202l2-1c5%200%2028-10%2028-12s-4-1-9%201m8%2017l-20%202-13%201-2%204c-4%2012-2%2024%206%2041s8%2017%2018%206c11-13%2018-25%2020-38%201-6%200-18-1-18l-8%202m50%2019c-7%204-8%205-8%2020v14c1%202%201-3%201-17%200-7%200-7%202-7%209%203%2021%203%2029%201%202%200%202%200%201%206-1%2010-3%2012-14%2012-7%200-9-1-12-3-5-2-5-3-4-1%203%205%2021%207%2027%203l3-2v3l-1%208c-2%202-1%203%201%201%202-1%202-2%202-13l1-16c1-5%200-6-10-9-9-2-13-2-18%200m2%201c-4%201-4%202-2%202%203%200%202%202-1%202-5%200-7%202-3%204%209%203%2029%202%2030-2%201-2-1-4-11-6s-9-2-13%200M37%20177l-1%203-1-2c0-1%200-2-1-1l-1%202-3%203-3%202%202%202c3%203%202%2012-2%2012h-2c0%201%201%202%204%202%202%200%203%201%203%202l1%202%201-2c0-3%200-3%203-3%204%200%209%202%209%204%200%201%201%201%202-1l4-2c4%200%205-3%202-6s-3-4-1-8l2-4c2%200%202%200%201-2h-4l-1-2-1%202-1%201c-3%200-4%203-3%205%202%203%202%204%201%208-1%206-3%207-4%202l-4-5c-4-4-2-11%203-10%201%201%202%200%202-1l-2-1-3-1-1-2c-1-1-1%200-1%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/82b99149d74ea13e7d8705ee269c48d0/ca0a1/fast-small-cheap.webp 223w", "/static/82b99149d74ea13e7d8705ee269c48d0/75680/fast-small-cheap.webp 445w", "/static/82b99149d74ea13e7d8705ee269c48d0/8d1ba/fast-small-cheap.webp 890w", "/static/82b99149d74ea13e7d8705ee269c48d0/3838e/fast-small-cheap.webp 1335w", "/static/82b99149d74ea13e7d8705ee269c48d0/cce66/fast-small-cheap.webp 1468w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/82b99149d74ea13e7d8705ee269c48d0/e92b6/fast-small-cheap.png 223w", "/static/82b99149d74ea13e7d8705ee269c48d0/e66bf/fast-small-cheap.png 445w", "/static/82b99149d74ea13e7d8705ee269c48d0/4ef49/fast-small-cheap.png 890w", "/static/82b99149d74ea13e7d8705ee269c48d0/4e814/fast-small-cheap.png 1335w", "/static/82b99149d74ea13e7d8705ee269c48d0/e4ba2/fast-small-cheap.png 1468w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/82b99149d74ea13e7d8705ee269c48d0/4ef49/fast-small-cheap.png",
                "alt": "Fast, small, cheap",
                "title": "Fast, small, cheap",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Fast, small, cheap`}</figcaption>{`
  `}</figure></p>
    <p>{`With metered pricing, serverless lets you pay directly for execution time, memory size, and storage space. No overhead. You don't use it, you don't pay it.`}</p>
    <p>{`AWS Lambda charges for execution time with `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/blogs/aws/new-for-aws-lambda-1ms-billing-granularity-adds-cost-savings/"
      }}>{`millisecond precision`}</a>{`.`}</p>
    <p><strong parentName="p">{`Faster code`}</strong>{` uses more memory to gain speed. Or you can beef up CPU. Both make your code more expensive to run.`}</p>
    <p><strong parentName="p">{`Saving storage space`}</strong>{` costs CPU time to compress and clean data. Storage is cheap. Bandwidth to and from storage gets pricey.`}</p>
    <p>{`You `}<strong parentName="p">{`optimize memory`}</strong>{` with slower code. Memory is plentiful these days, but not in serverless. You want small functions.`}</p>
    <p>{`Every optimization takes `}<strong parentName="p">{`human time and effort`}</strong>{`. A metric that software `}<em parentName="p">{`organizations`}</em>{` care about. How hard is the code to develop and maintain?`}</p>
    <p>{`You can think of performance as a 4-dimensional optimization function. `}<strong parentName="p">{`Speed vs. space vs. money vs. effort`}</strong>{`. Great question for a calculus exam.`}</p>
    <p>{`Avoid doing the calculus with ye olde law of prioritization:`}</p>
    <ol>
      <li parentName="ol">{`Make it work`}</li>
      <li parentName="ol">{`Make it right`}</li>
      <li parentName="ol">{`Make it fast`}</li>
    </ol>
    <p>{`You'll find that computers are fast & cheap and humans are slow & expensive. Stick to 1, add 2 for easier maintenance. 3 when necessary.`}</p>
    <h3 {...{
      "id": "where-size-matters"
    }}>{`Where size matters`}</h3>
    <p>{`Storage is cheap – `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/s3/pricing/"
      }}>{`$0.023/GB/month on S3`}</a>{` – and memory is `}<em parentName="p">{`pretty`}</em>{` cheap at `}<a parentName="p" {...{
        "href": "https://aws.amazon.com/lambda/pricing/"
      }}>{`$0.000016/GB/second`}</a>{`. Memory cost per unit `}<em parentName="p">{`increases`}</em>{` when you need extra. Storage cost per unit `}<em parentName="p">{`decreases`}</em>{` when you use more.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/eLvhchyvNNOuLbOtYP/giphy-loop.mp4?cid=4ac046a2qaldyq58jjtkqhe7edi39acwk4urhadsbcsbugg4&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><em parentName="p">{`Bandwidth`}</em>{` is where size gets you. AWS charges for taking data out of the system. Other providers are similar.`}</p>
    <p>{`You pay to send data to users or between availability zones. Details depend on which services are involved. For example: S3 to CloudFront (the CDN) is free, then CloudFront charges $0.085/GB.`}</p>
    <p>{`Minimize what you send over the wire.`}</p>
    <h2 {...{
      "id": "thinking-about-speed"
    }}>{`Thinking about speed`}</h2>
    <p>{`2 metrics matter for "speed":`}</p>
    <ol>
      <li parentName="ol">{`Latency`}</li>
      <li parentName="ol">{`Throughput`}</li>
    </ol>
    <p>{`Best summarized with a metaphor from `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Andrew_S._Tanenbaum"
      }}>{`Andrew S. Tanenbaum`}</a>{`:`}</p>
    <blockquote>
      <p parentName="blockquote">{`Never underestimate the bandwidth of a station wagon full of tapes hurtling down the highway`}</p>
    </blockquote>
    <div id="lock" />
    <h3 {...{
      "id": "latency"
    }}>{`Latency`}</h3>
    <p>{`Latency measures delay. How long does it take to start working?`}</p>
    <p>{`You can write the fastest code in the world, but if it takes 2 seconds to get started you'll have unhappy users.`}</p>
    <p>{`The biggest factors are:`}</p>
    <ul>
      <li parentName="ul">{`network time`}</li>
      <li parentName="ul">{`internal routing`}</li>
      <li parentName="ul">{`lambda wake up time`}</li>
    </ul>
    <p><strong parentName="p">{`Network time`}</strong>{` measures how long it takes for a user's request to reach your server. Depends on distance and connection quality.`}</p>
    <p><strong parentName="p">{`Routing`}</strong>{` is internal to your serverless provider. How long does it take to accept a request and send it to your code? Security rules can make this slower or faster.`}</p>
    <p><strong parentName="p">{`Lambda wake up time`}</strong>{` measures how long it takes to spin up your tiny server. Depends on bundle size, runtime environment, and how your code warms up.`}</p>
    <h3 {...{
      "id": "throughput"
    }}>{`Throughput`}</h3>
    <p>{`Throughput measures how fast you work. When you get a request, how long does it take?`}</p>
    <p>{`The biggest factors are:`}</p>
    <ul>
      <li parentName="ul">{`code performance`}</li>
      <li parentName="ul">{`input/output`}</li>
    </ul>
    <p>{`Serverless is for `}<a parentName="p" {...{
        "href": "/lambda-pipelines"
      }}>{`small distributed operations`}</a>{`. Code performance and algorithm complexity have little impact.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Fancy algorithms are slow when N is small, and N is usually small. Fancy algorithms have big constants. Until you know that N is frequently going to be big, don't get fancy.
~ `}<a parentName="p" {...{
          "href": "https://en.wikipedia.org/wiki/Rob_Pike"
        }}>{`Rob Pike`}</a></p>
    </blockquote>
    <p>{`That leaves `}<strong parentName="p">{`input/output`}</strong>{`.`}</p>
    <p>{`Waiting for a database, talking to 3rd party APIs, loading a web page, those will `}<em parentName="p">{`destroy`}</em>{` your performance. `}</p>
    <p>{`The average duration for my screenshot lambda is 10 seconds. Mostly waiting for Chrome to start and webpages to load.`}</p>
    <p>{`You pay for every millisecond of that wait. 🙃`}</p>
    <h2 {...{
      "id": "thinking-about-scalability"
    }}>{`Thinking about scalability`}</h2>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Scalability"
      }}>{`Scalability`}</a>{` talks about how your system behaves as load grows.`}</p>
    <p>{`What happens at 10 requests per day? What about 10,000,000?`}</p>
    <p>{`Your system is considered scalable, if resources grow linearly or less with demand. Using 1 lambda per request may not be cheap, but it does scale.`}</p>
    <p>{`A system that grows faster than demand blows up eventually.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "890px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/efad41dcd186a5f8159dee56e2bcea70/cb9f4/scalability.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "62.33183856502242%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'249\\'%20viewBox=\\'0%200%20400%20249\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M31%2026a5829%205829%200%2000-2%20105%201915%201915%200%2000-3%2080v13h-6c-6%200-9%201-7%203l2-1h9l2%202%201%203%201-2c0-2%201-3%202-3l9-1c39-5%20108-8%20153-6a2169%202169%200%200120-1c-49-3-114-1-176%206h-5l7-5a841%20841%200%20016-5l-15%2010-1-4%201-39a10296%2010296%200%20002-155m308%2088l-15%201a528%20528%200%200015-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/efad41dcd186a5f8159dee56e2bcea70/ca0a1/scalability.webp 223w", "/static/efad41dcd186a5f8159dee56e2bcea70/75680/scalability.webp 445w", "/static/efad41dcd186a5f8159dee56e2bcea70/8d1ba/scalability.webp 890w", "/static/efad41dcd186a5f8159dee56e2bcea70/3838e/scalability.webp 1335w", "/static/efad41dcd186a5f8159dee56e2bcea70/e11e5/scalability.webp 1780w", "/static/efad41dcd186a5f8159dee56e2bcea70/367e1/scalability.webp 1903w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/efad41dcd186a5f8159dee56e2bcea70/e92b6/scalability.png 223w", "/static/efad41dcd186a5f8159dee56e2bcea70/e66bf/scalability.png 445w", "/static/efad41dcd186a5f8159dee56e2bcea70/4ef49/scalability.png 890w", "/static/efad41dcd186a5f8159dee56e2bcea70/4e814/scalability.png 1335w", "/static/efad41dcd186a5f8159dee56e2bcea70/701e9/scalability.png 1780w", "/static/efad41dcd186a5f8159dee56e2bcea70/cb9f4/scalability.png 1903w"],
              "sizes": "(max-width: 890px) 100vw, 890px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/efad41dcd186a5f8159dee56e2bcea70/4ef49/scalability.png",
              "alt": "scalability",
              "title": "scalability",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`This applies to teams as much as it does to computers. If you need 3 new developers to do 2x more work, your company will die.`}</p>
    <p>{`Scaling your software comes in 2 flavors: vertical and horizontal. Use a mix of both.`}</p>
    <h3 {...{
      "id": "vertical-scaling"
    }}>{`Vertical scaling`}</h3>
    <p>{`Vertical scaling is the art of getting 1 computational resource to do more. `}</p>
    <p>{`This type of scaling can get expensive. You need more resources – faster CPU, more memory, better hardware, a GPU – and lots of engineering effort to optimize your code.`}</p>
    <p>{`For many workloads, this approach is best.`}</p>
    <p>{`It's easier to scale a database by adding CPU and memory than by rebuilding your application to use more databases. AI researchers prefer a computer with terabytes of memory over tweaking algorithms to need less.`}</p>
    <p>{`And sometimes it's the only way. Like processing a video.`}</p>
    <h3 {...{
      "id": "horizontal-scaling"
    }}>{`Horizontal scaling`}</h3>
    <p>{`Horizontal scaling is the art of splitting work between computational resources.`}</p>
    <p>{`This type of scaling can be cheap. Easier to provision, quicker to get going, less effort to optimize.`}</p>
    <p>{`But you have to find a balance. 6 cheap computers can cost more than 3 expensive computers.`}</p>
    <p>{`That's where serverless shines.`}</p>
    <p>{`Horizontal scaling is perfect for isolated operations with little inter-dependency. Like API requests, processing a video library, or serving static files. `}</p>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "/lambda-pipelines"
      }}>{`map-reduce pattern`}</a>{` to split larger tasks like we did in the Lambda Pipelines chapter. You pay with system complexity.`}</p>
    <h2 {...{
      "id": "achieving-speed"
    }}>{`Achieving speed`}</h2>
    <p>{`Never optimize your code until it tells you where it hurts. Bottlenecks are surprising and unpredictable. Measure.`}</p>
    <p>{`But don't kick the table with your pinky toe either. You already know that hurts.`}</p>
    <p>{`For maximum speed in serverless environments focus on:`}</p>
    <ul>
      <li parentName="ul">{`cold wake up times`}</li>
      <li parentName="ul">{`reducing input output`}</li>
      <li parentName="ul">{`fast simple code`}</li>
    </ul>
    <h3 {...{
      "id": "cold-wake-up-times"
    }}>{`Cold wake up times`}</h3>
    <p>{`Traditional servers split performance into warm and cold. The server starts cold and warms up its caches, algorithm setup, and execution environment with the first few requests.`}</p>
    <p>{`Most requests reach a warm server which is orders of magnitude faster.`}</p>
    <p>{`With serverless, every request could be cold.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/3oFzm0o2jMKftsaBoc/giphy-loop.mp4?cid=4ac046a2iowf4l9palengxxql4w9k2cvmv22bluvw5gmfxvn&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`There is no one solution I can give you. Optimizing cold boot performance takes work and understanding `}<em parentName="p">{`your`}</em>{` software.`}</p>
    <p>{`A few areas to look at:`}</p>
    <ul>
      <li parentName="ul">{`Use a language with a `}<strong parentName="li">{`fast and nimble runtime`}</strong>{`. JavaScript is surprisingly effective, Go can work great. JVM-based languages tend to struggle.`}</li>
      <li parentName="ul"><strong parentName="li">{`Ruthlessly reduce bundle size`}</strong>{`. The less code that needs to load, the better. Compile and minimize your source, `}<a parentName="li" {...{
          "href": "https://www.serverless.com/framework/docs/providers/aws/guide/packaging#package-configuration"
        }}>{`remove un-needed dependencies`}</a>{`. Use the `}<inlineCode parentName="li">{`exclude`}</inlineCode>{` config in `}<inlineCode parentName="li">{`serverless.yml`}</inlineCode>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`Avoid fancy algorithms`}</strong>{` with large constants. Iterating your data 5x to set up a fast algorithm may not be worth it. You're processing small payloads.`}</li>
    </ul>
    <h3 {...{
      "id": "reduce-input-output"
    }}>{`Reduce input output`}</h3>
    <p>{`The biggest performance killer is input/output. Your tiny server comes with no batteries included.`}</p>
    <p>{`Want to read from cache? Network request. Database? Network request. Call an API? Yep, network.`}</p>
    <p><a parentName="p" {...{
        "href": "https://gist.github.com/hellerbarde/2843375"
      }}>{`Networks are slow`}</a>{`. If reading a variable is like brushing your teeth, a roundtrip with your database is like a 6 day vacation. 🤯`}</p>
    <p><img parentName="p" {...{
        "src": "https://camo.githubusercontent.com/76ced2beb27b9053184da0cd7bf2d671f901d1521fcb35cb00d55631d22d5b85/687474703a2f2f692e696d6775722e636f6d2f6b307431652e706e67",
        "alt": "Latency numbers every programmer should know"
      }}></img></p>
    <p>{`Providers optimize these "external" requests but you have no control and there are hard limits at play.`}</p>
    <p>{`Best you can do is minimize.`}</p>
    <ul>
      <li parentName="ul">{`do more in 1 request (like bigger SQL queries)`}</li>
      <li parentName="ul">{`avoid I/O in a loop, try to pre-fetch`}</li>
      <li parentName="ul">{`prefer local `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Memoization"
        }}>{`memoization`}</a>{` over cache`}</li>
    </ul>
    <h4 {...{
      "id": "memoization-vs-cache"
    }}>{`Memoization vs. cache`}</h4>
    <p>{`Memoization stores results in a local variable. Call your function with the same inputs and return the value without recalculating.`}</p>
    <p>{`But you lose memory when your lambda goes to bed. That's where a cache can help. A service with persistent memory that stores pre-computed values.`}</p>
    <p>{`Like your database, the caching service is an external request. Much slower than a local variable.`}</p>
    <p>{`Use memoization when you call the same code multiple times per request. Use cache when you need the same data across many requests.`}</p>
    <p>{`When you get requests frequently enough, serverless providers reuse your code between executions. Memoizing in "global" memory does wonders.`}</p>
    <p>{`In pseudo-javascript:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`let memoized = null

exports.handler = function (argument) {
  if (!memoized) {
    memoized = expensiveComputation()
  }

  return memoized
}
`}</code></pre>
    <h3 {...{
      "id": "fast-simple-code"
    }}>{`Fast simple code`}</h3>
    <p>{`Keep it simple.`}</p>
    <p>{`Use the fastest check as your first argument in an `}<inlineCode parentName="p">{`&&`}</inlineCode>{` or `}<inlineCode parentName="p">{`||`}</inlineCode>{` chain. Computers skip arguments that can't change the result.`}</p>
    <p>{`Avoid doing work that you'll throw away 10 lines later.`}</p>
    <p>{`An `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{` search can be faster than building a hashmap in `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{` and reading it once.`}</p>
    <h2 {...{
      "id": "think-of-your-system-as-a-whole"
    }}>{`Think of your system as a whole`}</h2>
    <p>{`Once you've optimized individual components, it's time to look at your system as a whole. Find the bottlenecks.`}</p>
    <blockquote>
      <p parentName="blockquote">{`The fastest algorithm in the world is worthless when throttled by a slow database. `}</p>
    </blockquote>
    <p>{`A bottleneck happens when there's a performance mismatch between parts of your system. Fast code feeding into slow code. Slow code that your whole system relies on ...`}</p>
    <p>{`Typical offenders include:`}</p>
    <ul>
      <li parentName="ul">{`large computation (like video and image processing)`}</li>
      <li parentName="ul">{`poorly optimized databases`}</li>
      <li parentName="ul">{`3rd party APIs`}</li>
    </ul>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/37e57983589c71f83228db14c5f5f486/1e1c3/spreading-slowness.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "69.05829596412556%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'277\\'%20viewBox=\\'0%200%20400%20277\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M95%2022v1c1%201-2%207-3%207l-1-2c0-2-2%200-2%203-1%203-4%204-7%202-1-1-11%200-12%202%200%202-1%204-5%206-5%203-5%203-5-1%201-3%200-4-1-1-2%205%201%207%205%204l7-1c4-1%209-3%209-6%200-2%200-2%203-1%205%201%206%201%207-1%200-2%201-3%202-2%202%200%203-1%204-5l1-3v2c0%204%202%204%205%203%204-1%203-3-2-6-4-2-5-2-5-1m135%20224c-2%206-1%207%205%207%204%200%206-2%204-4v-3c1-1%201-1-1-3-4-3-7-3-8%203m13-1c-3%204-2%205%205%205%205%200%208-3%207-6l2-1c2%200%202%200%201%202v6l1-2c0-4%202-5%206-5%203%200%203%200%203%204l-1%204%202-4c1-2%202-3%204-3l5-1-3-1-4-1h-21c-2-2-4-1-7%203\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37e57983589c71f83228db14c5f5f486/ca0a1/spreading-slowness.webp 223w", "/static/37e57983589c71f83228db14c5f5f486/75680/spreading-slowness.webp 445w", "/static/37e57983589c71f83228db14c5f5f486/8d1ba/spreading-slowness.webp 890w", "/static/37e57983589c71f83228db14c5f5f486/3838e/spreading-slowness.webp 1335w", "/static/37e57983589c71f83228db14c5f5f486/5afe4/spreading-slowness.webp 1670w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/37e57983589c71f83228db14c5f5f486/e92b6/spreading-slowness.png 223w", "/static/37e57983589c71f83228db14c5f5f486/e66bf/spreading-slowness.png 445w", "/static/37e57983589c71f83228db14c5f5f486/4ef49/spreading-slowness.png 890w", "/static/37e57983589c71f83228db14c5f5f486/4e814/spreading-slowness.png 1335w", "/static/37e57983589c71f83228db14c5f5f486/1e1c3/spreading-slowness.png 1670w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/37e57983589c71f83228db14c5f5f486/4ef49/spreading-slowness.png",
                "alt": "Bottlenecks impact your whole system",
                "title": "Bottlenecks impact your whole system",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Bottlenecks impact your whole system`}</figcaption>{`
  `}</figure></p>
    <p>{`You can find the bottleneck by looking at your queues. Is one of them filling up with data? Likely feeding a bottleneck.`}</p>
    <p>{`You can look at Lambda execution times. A slow Lambda could be full of bad code, more likely it's talking to a bottleneck. `}</p>
    <p>{`Can you move it off the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Critical_path_method"
      }}>{`critical path`}</a>{`? Cache or memoize any API and database responses?`}</p>
    <p>{`The exact answer depends on `}<em parentName="p">{`your`}</em>{` code and `}<em parentName="p">{`your`}</em>{` system.`}</p>
    <h2 {...{
      "id": "optimizing-cost"
    }}>{`Optimizing cost`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Value your time.`}</p>
    </blockquote>
    <p>{`I talked to an AWS billing expert and that was the take-away. Then I killed the whole chapter on cost.`}</p>
    <p>{`Your time is worth more than your bill.`}</p>
    <p>{`However, he suggested you try `}<a parentName="p" {...{
        "href": "https://github.com/alexcasalboni/aws-lambda-power-tuning"
      }}>{`AWS Lambda Power Tuning`}</a>{`. A tool that tries your lambda in different configurations and shows the balance between power, speed, and cost.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/3c1b97c4155238cec09a7fee21764985/b7f47/aws-lambda-power-tuning.jpg",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "53.81165919282511%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'216\\'%20viewBox=\\'0%200%20400%20216\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M111%2024c-1%206-1%206%2012%207h12v-8l-12-1c-11%200-11%200-12%202m86%203v4h23v-8h-23v4M58%2036l3%205%2010%2013a19367%2019367%200%200123%2031l5%207-16-1a489%20489%200%2001-24%201c0%201%202%202%2021%202h20l7%207%206%208%201%201a242%20242%200%2000134%2056c11%200%2013%201%2014%205%200%205%200%205%202%208%202%204%201-1%200-5-3-7-3-7%207-7a487%20487%200%200048%200h-17v-4c2-9%202-25%201-32v6l-1%2021-1%209-3-1-37-1-1-6-1-5v5l1%206-5-1a179%20179%200%2001-30-4%20197%20197%200%2001-56-19%20170%20170%200%2001-65-47l11%201h2a651%20651%200%2001146%2015v3l1-3c0-2%201-3%203-3%206%200%2015-2%2016-3%203-3%209-5%2010-4%202%200%202%200%201-1%200-1%201-3%204-5s12-10%2020-19l18-17c2-1%203-3%201-4-1%200-6%204-11%2010-18%2018-30%2030-33%2031l-14%208c-8%203-28%202-50-4h-6l-22-2a521%20521%200%2000-81-5h-18l-3-4-7-9a474%20474%200%2000-23-32c-3-4-6-8-6-10-1-2-3-3-5-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/3c1b97c4155238cec09a7fee21764985/ca0a1/aws-lambda-power-tuning.webp 223w", "/static/3c1b97c4155238cec09a7fee21764985/75680/aws-lambda-power-tuning.webp 445w", "/static/3c1b97c4155238cec09a7fee21764985/8d1ba/aws-lambda-power-tuning.webp 890w", "/static/3c1b97c4155238cec09a7fee21764985/3838e/aws-lambda-power-tuning.webp 1335w", "/static/3c1b97c4155238cec09a7fee21764985/e11e5/aws-lambda-power-tuning.webp 1780w", "/static/3c1b97c4155238cec09a7fee21764985/26151/aws-lambda-power-tuning.webp 1804w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/3c1b97c4155238cec09a7fee21764985/853b2/aws-lambda-power-tuning.jpg 223w", "/static/3c1b97c4155238cec09a7fee21764985/0a4fe/aws-lambda-power-tuning.jpg 445w", "/static/3c1b97c4155238cec09a7fee21764985/7f80b/aws-lambda-power-tuning.jpg 890w", "/static/3c1b97c4155238cec09a7fee21764985/dc6ba/aws-lambda-power-tuning.jpg 1335w", "/static/3c1b97c4155238cec09a7fee21764985/2d2e5/aws-lambda-power-tuning.jpg 1780w", "/static/3c1b97c4155238cec09a7fee21764985/b7f47/aws-lambda-power-tuning.jpg 1804w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/jpeg"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/3c1b97c4155238cec09a7fee21764985/7f80b/aws-lambda-power-tuning.jpg",
                "alt": "Example graph from AWS Lambda Power Tuning",
                "title": "Example graph from AWS Lambda Power Tuning",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Example graph from AWS Lambda Power Tuning`}</figcaption>{`
  `}</figure></p>
    <p>{`Execution time goes from 35s with 128MB to less than 3s with 1.5GB, while being 14% cheaper to run.`}</p>
    <p>{`A great example of how vertical scaling beats horizontal. But you keep every benefit of horizontal, because serverless. 🚀`}</p>
    <p>{`Next chapter we look at running Serverless Chrome, a typical case where beefy lambdas help lots.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      